import { useState } from "react";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import type { TestSessionSummary } from "@school-sales-ai/shared";
import { ScoreBadge } from "@school-sales-ai/shared/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTestSessions } from "@/hooks/useTestSessions";
import { RenameTestSessionDialog } from "./RenameTestSessionDialog";
import { DeleteTestSessionDialog } from "./DeleteTestSessionDialog";

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })} ${d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}`;
}

function sessionTitle(s: TestSessionSummary): string {
  return s.testLabel ?? `Prueba ${s.sessionRef}`;
}

type Props = {
  onOpen: (session: TestSessionSummary) => void;
  onNew: () => void;
};

export function TestSessionList({ onOpen, onNew }: Props): React.JSX.Element {
  const { data, isLoading, isError } = useTestSessions();
  const [renameSession, setRenameSession] = useState<TestSessionSummary | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const items = data ?? [];

  return (
    <TooltipProvider>
      <div className="flex items-center justify-between p-4 md:px-6 md:pt-6">
        <h2 className="text-sm font-semibold text-ink-700">Chats de prueba</h2>
        <button
          type="button"
          onClick={onNew}
          className="inline-flex items-center gap-1.5 rounded-lg bg-lime px-3 py-2 text-[13px] font-semibold text-moss hover:bg-lime-dk transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nueva prueba
        </button>
      </div>

      {isLoading ? (
        <p className="p-6 text-sm text-ink-400">Cargando chats de prueba…</p>
      ) : isError ? (
        <p className="p-6 text-sm text-red-600">
          No se pudieron cargar los chats de prueba.
        </p>
      ) : items.length === 0 ? (
        <p className="p-6 text-sm text-ink-400">
          Aún no hay chats de prueba. Crea uno con “Nueva prueba”.
        </p>
      ) : (
        <div className="p-4 md:px-6 md:pb-6">
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead className="text-center">Score</TableHead>
                  <TableHead className="text-center">Mensajes</TableHead>
                  <TableHead>Última actividad</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium text-ink-800">
                      <button
                        type="button"
                        onClick={() => onOpen(s)}
                        className="text-left hover:text-moss hover:underline"
                      >
                        {sessionTitle(s)}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-ink-600">
                      {s.sessionRef}
                    </TableCell>
                    <TableCell className="text-center">
                      <ScoreBadge score={s.score} size="sm" />
                    </TableCell>
                    <TableCell className="text-center text-ink-600">
                      {s.messageCount}
                    </TableCell>
                    <TableCell className="text-ink-600">
                      {formatDateTime(s.lastInteractionAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <IconButton label="Ver" onClick={() => onOpen(s)}>
                          <Eye className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                          label="Renombrar"
                          onClick={() => setRenameSession(s)}
                        >
                          <Pencil className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                          label="Eliminar"
                          onClick={() => setDeleteId(s.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="flex flex-col gap-3 md:hidden">
            {items.map((s) => (
              <div
                key={s.id}
                className="rounded-xl border border-ink-100 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => onOpen(s)}
                    className="text-left text-sm font-semibold text-ink-800"
                  >
                    {sessionTitle(s)}
                  </button>
                  <ScoreBadge score={s.score} size="sm" />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500">
                  <span className="font-mono">{s.sessionRef}</span>
                  <span>{s.messageCount} mensajes</span>
                  <span>{formatDateTime(s.lastInteractionAt)}</span>
                </div>
                <div className="mt-3 flex gap-3 text-xs font-medium">
                  <button
                    type="button"
                    onClick={() => onOpen(s)}
                    className="text-moss"
                  >
                    Ver
                  </button>
                  <button
                    type="button"
                    onClick={() => setRenameSession(s)}
                    className="text-ink-600"
                  >
                    Renombrar
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteId(s.id)}
                    className="text-red-600"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {renameSession && (
        <RenameTestSessionDialog
          sessionId={renameSession.id}
          currentLabel={renameSession.testLabel}
          open={true}
          onClose={() => setRenameSession(null)}
        />
      )}
      {deleteId && (
        <DeleteTestSessionDialog
          sessionId={deleteId}
          open={true}
          onClose={() => setDeleteId(null)}
        />
      )}
    </TooltipProvider>
  );
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className="rounded-md p-1.5 text-ink-500 hover:bg-ink-050 transition-colors"
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
